/**
 * Sub-spec 08 Plan 3 §3.1 — scheduled reports settings page.
 *
 * Lists existing scheduled reports with quick edit/pause/delete actions.
 * Creating a new schedule is done from the per-report page via the
 * "Schedule this report" drawer (richer context — the user is looking at
 * the report when they decide to recur it).
 */
import { useConfirm } from '@/components/Confirm';
import { Calendar, Pause, Play, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { type ReportSlug, getReportEntry } from '../../reports/registry';

interface ScheduledReport {
  id: string;
  reportType: string;
  schedule: 'daily' | 'weekly' | 'monthly';
  sendHour: number;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  recipientMembershipIds: string[];
  isActive: number;
  lastRunAt: string | null;
  createdAt: string;
}

function scheduleLabel(r: ScheduledReport): string {
  const time = `${String(r.sendHour).padStart(2, '0')}:00`;
  if (r.schedule === 'daily') return `Daily at ${time}`;
  if (r.schedule === 'weekly') {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `Weekly on ${days[r.dayOfWeek ?? 0]} at ${time}`;
  }
  return `Monthly on day ${r.dayOfMonth ?? 1} at ${time}`;
}

function reportTitle(slug: string): string {
  const entry = getReportEntry(slug);
  return entry?.title ?? slug;
}

export function ScheduledReportsPage() {
  const [rows, setRows] = useState<ScheduledReport[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await fetch('/api/scheduled-reports', { credentials: 'include' });
      if (res.status === 403) {
        setErr('You need the reports.configure capability to manage scheduled reports.');
        setRows([]);
        return;
      }
      if (!res.ok) {
        setErr(`Failed to load (${res.status}).`);
        setRows([]);
        return;
      }
      const body = (await res.json()) as { scheduledReports: ScheduledReport[] };
      setRows(body.scheduledReports);
    } catch {
      setErr('Network error.');
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function pauseOrResume(id: string, active: boolean) {
    const url = `/api/scheduled-reports/${id}/${active ? 'pause' : 'resume'}`;
    const res = await fetch(url, { method: 'POST', credentials: 'include' });
    if (res.ok) await load();
  }

  async function remove(id: string) {
    const yes = await confirm({
      title: 'Delete scheduled report?',
      message: 'Recipients will stop receiving it.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!yes) return;
    const res = await fetch(`/api/scheduled-reports/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (res.ok) await load();
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Scheduled reports</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Recurring CSV exports emailed to selected staff members on your account's local time.
          Create a new schedule from any report's page via "Schedule this report".
        </p>
      </header>

      {err && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {err}
        </div>
      )}

      {rows === null ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface p-10 text-center">
          <Calendar className="mx-auto mb-3 size-8 text-muted-foreground" strokeWidth={1.5} />
          <h2 className="text-base font-medium tracking-tight">No scheduled reports yet</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Open a report from the{' '}
            <Link to="/reports" className="text-primary underline underline-offset-2">
              Reports
            </Link>{' '}
            section and click "Schedule this report" to start one.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-secondary/40 text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
              <tr>
                <th className="border-b border-border px-3 py-2 text-left font-medium">Report</th>
                <th className="border-b border-border px-3 py-2 text-left font-medium">Schedule</th>
                <th className="border-b border-border px-3 py-2 text-left font-medium">
                  Recipients
                </th>
                <th className="border-b border-border px-3 py-2 text-left font-medium">Status</th>
                <th className="border-b border-border px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0">
                  <td className="px-3 py-2">
                    <Link to={`/reports/${r.reportType}`} className="text-primary hover:underline">
                      {reportTitle(r.reportType)}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{scheduleLabel(r)}</td>
                  <td className="px-3 py-2 num">{r.recipientMembershipIds.length}</td>
                  <td className="px-3 py-2">
                    {r.isActive ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900">
                        Active
                      </span>
                    ) : (
                      <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        Paused
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => pauseOrResume(r.id, Boolean(r.isActive))}
                        aria-label={r.isActive ? 'Pause' : 'Resume'}
                        className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary"
                      >
                        {r.isActive ? (
                          <Pause className="size-4" strokeWidth={1.75} />
                        ) : (
                          <Play className="size-4" strokeWidth={1.75} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(r.id)}
                        aria-label="Delete"
                        className="grid size-8 place-items-center rounded-md text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="size-4" strokeWidth={1.75} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Re-export the ReportSlug type for type-only consumers.
export type { ReportSlug };
